import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  Wallet,
  Target,
  Percent,
  Sparkles,
  Instagram,
  Facebook,
  Linkedin,
  Youtube,
  Music2,
  Plus,
  Trash2,
} from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
  BarChart,
  Bar,
  Legend,
} from "recharts";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import api from "@/lib/api";
import { toast } from "sonner";

const MONTHS = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

const NETWORK_META = {
  instagram: { label: "Instagram", Icon: Instagram, color: "#C13584" },
  facebook: { label: "Facebook", Icon: Facebook, color: "#1877F2" },
  linkedin: { label: "LinkedIn", Icon: Linkedin, color: "#0A66C2" },
  youtube: { label: "YouTube", Icon: Youtube, color: "#FF0000" },
  threads: { label: "Threads", Icon: Sparkles, color: "#000000" },
  tiktok: { label: "TikTok", Icon: Music2, color: "#000000" },
};

const PLATFORMS = [
  { value: "meta", label: "Meta (IG + FB)" },
  { value: "google", label: "Google Ads" },
  { value: "tiktok", label: "TikTok Ads" },
  { value: "linkedin", label: "LinkedIn Ads" },
  { value: "other", label: "Outro" },
];

const eur = (n) =>
  new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Number(n) || 0);
const pct = (n) => (n === null || n === undefined ? "—" : `${n > 0 ? "+" : ""}${Number(n).toFixed(1)}%`);

function KpiCard({ title, value, delta, accent = "#3B2B23", icon: Icon }) {
  const positive = delta !== null && delta !== undefined && delta >= 0;
  return (
    <Card className="card-soft p-5 border-[#EAE3DB]">
      <div className="flex items-start justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#8B5E3C]">{title}</div>
        {Icon ? <Icon className="w-4 h-4 text-[#8B5E3C]" /> : null}
      </div>
      <div className="text-3xl font-bold text-[#1A1412]" style={{ color: accent }} data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, "-")}`}>
        {value}
      </div>
      {delta !== null && delta !== undefined ? (
        <div className={`mt-2 text-[11px] tracking-wide flex items-center gap-1 ${positive ? "text-emerald-700" : "text-red-700"}`}>
          {positive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          {pct(delta)} <span className="text-[#8B5E3C]">vs mês anterior</span>
        </div>
      ) : null}
    </Card>
  );
}

function SectionTitle({ children }) {
  return <h2 className="text-xs uppercase tracking-[0.3em] text-[#8B5E3C] mb-3">{children}</h2>;
}

export default function AdminClientDashboard() {
  const { clientId } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const now = new Date();
  const year = parseInt(params.get("year") || now.getFullYear(), 10);
  const month = parseInt(params.get("month") || (now.getMonth() + 1), 10);

  const [client, setClient] = useState(null);
  const [data, setData] = useState(null);
  const [feeInfo, setFeeInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  const setPeriod = (y, m) => {
    setParams({ year: String(y), month: String(m) });
  };

  const reload = async () => {
    setLoading(true);
    try {
      const [dash, feeR, cli] = await Promise.all([
        api.get(`/analytics/${clientId}/dashboard`, { params: { year, month } }),
        api.get(`/analytics/${clientId}/fee`),
        api.get(`/clients/${clientId}`),
      ]);
      setData(dash.data);
      setFeeInfo(feeR.data);
      setClient(cli.data);
    } catch (e) {
      toast.error("Erro a carregar o dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, year, month]);

  const evolutionData = useMemo(() => {
    if (!data?.evolution) return [];
    return data.evolution.map((e) => ({
      period: `${MONTHS[e.month - 1].slice(0, 3)}/${String(e.year).slice(2)}`,
      Receita: e.revenue_total,
      Investimento: e.investment_total,
      ROAS: e.roas_total,
      ROI: e.roi,
    }));
  }, [data]);

  if (loading) {
    return (
      <Layout>
        <div className="text-[#5C4A43] text-sm uppercase tracking-widest text-center py-20">A carregar dashboard…</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <button
              onClick={() => navigate("/admin")}
              className="text-[10px] uppercase tracking-[0.25em] text-[#8B5E3C] hover:text-[#3B2B23] flex items-center gap-1 mb-2"
              data-testid="dashboard-back"
            >
              <ArrowLeft className="w-3 h-3" /> Voltar aos clientes
            </button>
            <h1 className="text-3xl md:text-4xl font-bold text-[#1A1412]" data-testid="dashboard-client-name">
              {client?.name || "Cliente"}
            </h1>
            <div className="text-[11px] tracking-widest uppercase text-[#8B5E3C] mt-1">Dashboard de performance</div>
          </div>
          <div className="flex gap-2 items-end">
            <div>
              <Label className="text-[9px] tracking-[0.25em] uppercase text-[#8B5E3C] mb-1 block">Mês</Label>
              <Select value={String(month)} onValueChange={(v) => setPeriod(year, parseInt(v, 10))}>
                <SelectTrigger className="w-[150px] bg-white border-[#EAE3DB] rounded-full" data-testid="period-month">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS.map((m, i) => (
                    <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[9px] tracking-[0.25em] uppercase text-[#8B5E3C] mb-1 block">Ano</Label>
              <Select value={String(year)} onValueChange={(v) => setPeriod(parseInt(v, 10), month)}>
                <SelectTrigger className="w-[110px] bg-white border-[#EAE3DB] rounded-full" data-testid="period-year">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[year - 1, year, year + 1].map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* KPI Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            title="Receita"
            value={eur(data.kpis.revenue_total)}
            delta={data.deltas.revenue_total_pct}
            accent="#2C543B"
            icon={Wallet}
          />
          <KpiCard
            title="Investimento"
            value={eur(data.kpis.investment_total)}
            delta={data.deltas.ad_spend_pct}
            accent="#3B2B23"
            icon={Target}
          />
          <KpiCard
            title="ROAS Total"
            value={`${(data.kpis.roas_total || 0).toFixed(2)}×`}
            delta={data.deltas.roas_total_pct}
            accent="#C07C5B"
            icon={TrendingUp}
          />
          <KpiCard
            title="ROI"
            value={pct(data.kpis.roi)}
            delta={data.deltas.roi_pct}
            accent={data.kpis.roi >= 0 ? "#2C543B" : "#8A2A22"}
            icon={Percent}
          />
        </div>

        {/* Evolution charts */}
        <Card className="card-soft p-6 border-[#EAE3DB]">
          <SectionTitle>Evolução — últimos 6 meses</SectionTitle>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-[#5C4A43] mb-2">Receita vs Investimento</div>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={evolutionData} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EAE3DB" />
                    <XAxis dataKey="period" tick={{ fill: "#5C4A43", fontSize: 10 }} />
                    <YAxis tick={{ fill: "#5C4A43", fontSize: 10 }} />
                    <Tooltip formatter={(v) => eur(v)} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Area type="monotone" dataKey="Receita" stroke="#2C543B" fill="#2C543B" fillOpacity={0.15} strokeWidth={2} />
                    <Area type="monotone" dataKey="Investimento" stroke="#C07C5B" fill="#C07C5B" fillOpacity={0.15} strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-[#5C4A43] mb-2">ROAS e ROI</div>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={evolutionData} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EAE3DB" />
                    <XAxis dataKey="period" tick={{ fill: "#5C4A43", fontSize: 10 }} />
                    <YAxis yAxisId="l" tick={{ fill: "#5C4A43", fontSize: 10 }} />
                    <YAxis yAxisId="r" orientation="right" tick={{ fill: "#5C4A43", fontSize: 10 }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar yAxisId="l" dataKey="ROAS" fill="#3B2B23" radius={[4, 4, 0, 0]} />
                    <Bar yAxisId="r" dataKey="ROI" fill="#C07C5B" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </Card>

        {/* Investment breakdown + Fee editor */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <FeeCard clientId={clientId} year={year} month={month} feeInfo={feeInfo} onSaved={reload} />
          <CreativeCostsCard clientId={clientId} year={year} month={month} items={data.creative_costs} onSaved={reload} />
          <RevenueCard clientId={clientId} year={year} month={month} entries={data.revenue.entries} onSaved={reload} />
        </div>

        {/* Campaigns */}
        <CampaignsCard clientId={clientId} year={year} month={month} items={data.campaigns} onSaved={reload} />

        {/* Networks — aggregated overview + evolution */}
        <NetworksAggregateCard totals={data.network_totals} evolution={data.network_evolution} />

        {/* Networks grid */}
        <NetworksCard clientId={clientId} year={year} month={month} networks={data.networks} onSaved={reload} />
      </section>
    </Layout>
  );
}

/* ------------------------------ Sub-components ---------------------------- */

function NetworksAggregateCard({ totals, evolution }) {
  if (!totals) return null;
  const nice = (n) => (Number(n) || 0).toLocaleString("pt-PT");
  const chartData = (evolution || []).map((e) => ({
    period: `${MONTHS[e.month - 1].slice(0, 3)}/${String(e.year).slice(2)}`,
    Seguidores: e.total_followers,
    Reach: e.total_reach,
  }));
  const d = totals.deltas || {};
  return (
    <Card className="card-soft p-6 border-[#EAE3DB]" data-testid="networks-aggregate-card">
      <div className="flex items-center justify-between mb-4">
        <SectionTitle>Redes sociais — visão agrupada</SectionTitle>
        <div className="text-[10px] uppercase tracking-widest text-[#8B5E3C]">
          {totals.network_count} rede(s)
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="rounded-xl border border-[#EAE3DB] bg-white p-4">
          <div className="text-[9px] uppercase tracking-[0.25em] text-[#8B5E3C]">Seguidores totais</div>
          <div className="text-2xl font-bold text-[#1A1412] mt-1" data-testid="agg-total-followers">{nice(totals.total_followers)}</div>
          {d.followers_pct !== null && d.followers_pct !== undefined ? (
            <div className={`text-[10px] mt-1 flex items-center gap-1 ${d.followers_pct >= 0 ? "text-emerald-700" : "text-red-700"}`}>
              {d.followers_pct >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {pct(d.followers_pct)} <span className="text-[#8B5E3C]">vs mês anterior</span>
            </div>
          ) : null}
        </div>
        <div className="rounded-xl border border-[#EAE3DB] bg-white p-4">
          <div className="text-[9px] uppercase tracking-[0.25em] text-[#8B5E3C]">Reach total</div>
          <div className="text-2xl font-bold text-[#1A1412] mt-1" data-testid="agg-total-reach">{nice(totals.total_reach)}</div>
          {d.reach_pct !== null && d.reach_pct !== undefined ? (
            <div className={`text-[10px] mt-1 flex items-center gap-1 ${d.reach_pct >= 0 ? "text-emerald-700" : "text-red-700"}`}>
              {d.reach_pct >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {pct(d.reach_pct)}
            </div>
          ) : null}
        </div>
        <div className="rounded-xl border border-[#EAE3DB] bg-white p-4">
          <div className="text-[9px] uppercase tracking-[0.25em] text-[#8B5E3C]">Impressões totais</div>
          <div className="text-2xl font-bold text-[#1A1412] mt-1" data-testid="agg-total-impressions">{nice(totals.total_impressions)}</div>
          {d.impressions_pct !== null && d.impressions_pct !== undefined ? (
            <div className={`text-[10px] mt-1 flex items-center gap-1 ${d.impressions_pct >= 0 ? "text-emerald-700" : "text-red-700"}`}>
              {d.impressions_pct >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {pct(d.impressions_pct)}
            </div>
          ) : null}
        </div>
        <div className="rounded-xl border border-[#EAE3DB] bg-white p-4">
          <div className="text-[9px] uppercase tracking-[0.25em] text-[#8B5E3C]">Engagement médio</div>
          <div className="text-2xl font-bold text-[#1A1412] mt-1" data-testid="agg-avg-engagement">{(totals.avg_engagement || 0).toFixed(2)}%</div>
          {d.engagement_pct !== null && d.engagement_pct !== undefined ? (
            <div className={`text-[10px] mt-1 flex items-center gap-1 ${d.engagement_pct >= 0 ? "text-emerald-700" : "text-red-700"}`}>
              {d.engagement_pct >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {pct(d.engagement_pct)}
            </div>
          ) : null}
          <div className="text-[9px] mt-1 text-[#8B5E3C]">ponderado pelos seguidores</div>
        </div>
      </div>
      <div className="text-[11px] uppercase tracking-widest text-[#5C4A43] mb-2">Evolução da audiência — últimos 6 meses</div>
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EAE3DB" />
            <XAxis dataKey="period" tick={{ fill: "#5C4A43", fontSize: 10 }} />
            <YAxis tick={{ fill: "#5C4A43", fontSize: 10 }} />
            <Tooltip formatter={(v) => nice(v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Area type="monotone" dataKey="Seguidores" stroke="#3B2B23" fill="#3B2B23" fillOpacity={0.18} strokeWidth={2} />
            <Area type="monotone" dataKey="Reach" stroke="#C07C5B" fill="#C07C5B" fillOpacity={0.15} strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

function FeeCard({ clientId, year, month, feeInfo, onSaved }) {
  const [openEdit, setOpenEdit] = useState(false);
  const [amount, setAmount] = useState("");
  const [isDefault, setIsDefault] = useState(true);
  const [saving, setSaving] = useState(false);

  const monthOverride = (feeInfo?.overrides || []).find((o) => o.year === year && o.month === month);
  const effective = monthOverride?.amount ?? feeInfo?.default?.amount ?? 0;

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/analytics/${clientId}/fee`, {
        amount: parseFloat(amount || 0),
        is_default: isDefault,
        year: isDefault ? null : year,
        month: isDefault ? null : month,
      });
      toast.success("Fee guardado");
      setOpenEdit(false);
      onSaved();
    } catch {
      toast.error("Erro a guardar o fee");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="card-soft p-5 border-[#EAE3DB]" data-testid="fee-card">
      <SectionTitle>Fee mensal</SectionTitle>
      <div className="text-3xl font-bold text-[#1A1412] mb-1">{eur(effective)}</div>
      <div className="text-[11px] text-[#8B5E3C] mb-4">
        {monthOverride ? "Override para este mês" : (feeInfo?.default ? "Fixo mensal" : "Sem fee definido")}
      </div>
      {!openEdit ? (
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => {
              setIsDefault(true);
              setAmount(String(feeInfo?.default?.amount || ""));
              setOpenEdit(true);
            }}
            className="flex-1 rounded-full border-[#3B2B23] text-[#3B2B23] h-9 text-[10px] tracking-widest uppercase"
            data-testid="fee-edit-default"
          >
            Definir fixo
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              setIsDefault(false);
              setAmount(String(monthOverride?.amount || ""));
              setOpenEdit(true);
            }}
            className="flex-1 rounded-full border-[#3B2B23] text-[#3B2B23] h-9 text-[10px] tracking-widest uppercase"
            data-testid="fee-edit-override"
          >
            Só este mês
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          <Input
            type="number"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="€ 0,00"
            className="bg-white border-[#EAE3DB]"
            data-testid="fee-amount-input"
          />
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setOpenEdit(false)}
              className="flex-1 rounded-full border-[#3B2B23] h-9 text-[10px] tracking-widest uppercase"
            >
              Cancelar
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              className="flex-1 rounded-full bg-[#3B2B23] text-white h-9 text-[10px] tracking-widest uppercase"
              data-testid="fee-save"
            >
              Guardar
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

function CreativeCostsCard({ clientId, year, month, items, onSaved }) {
  const [label, setLabel] = useState("");
  const [amount, setAmount] = useState("");
  const [monthly, setMonthly] = useState(false);
  const total = (items || []).reduce((s, i) => s + Number(i.amount || 0), 0);

  const add = async () => {
    if (!label.trim() || !amount) return;
    try {
      await api.post(`/analytics/${clientId}/creative-costs`, {
        year, month, label: label.trim(), amount: parseFloat(amount), is_monthly_total: monthly,
      });
      setLabel(""); setAmount(""); setMonthly(false);
      onSaved();
    } catch { toast.error("Erro a adicionar custo"); }
  };
  const del = async (id) => {
    try { await api.delete(`/analytics/creative-costs/${id}`); onSaved(); }
    catch { toast.error("Erro a apagar"); }
  };

  return (
    <Card className="card-soft p-5 border-[#EAE3DB]" data-testid="creative-costs-card">
      <SectionTitle>Custos criativos</SectionTitle>
      <div className="text-3xl font-bold text-[#1A1412] mb-4">{eur(total)}</div>
      <div className="space-y-1 max-h-40 overflow-y-auto mb-3">
        {(items || []).map((i) => (
          <div key={i.id} className="flex items-center justify-between text-[12px] py-1 border-b border-[#EAE3DB]/60">
            <div className="notranslate truncate flex-1 pr-2" translate="no">
              {i.label}
              {i.is_monthly_total ? <span className="text-[9px] text-[#8B5E3C] ml-1 uppercase tracking-widest">total</span> : null}
            </div>
            <div className="font-semibold text-[#3B2B23]">{eur(i.amount)}</div>
            <button onClick={() => del(i.id)} className="ml-2 text-red-700 hover:text-red-900" data-testid={`creative-delete-${i.id}`}>
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))}
        {(!items || items.length === 0) && <div className="text-[11px] text-[#8B5E3C] italic">Ainda sem custos criativos</div>}
      </div>
      <div className="space-y-2">
        <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Ex: Sessão fotográfica" className="h-9 bg-white border-[#EAE3DB]" data-testid="creative-label" />
        <div className="flex gap-2">
          <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="€ 0,00" className="h-9 bg-white border-[#EAE3DB] flex-1" data-testid="creative-amount" />
          <Button onClick={add} className="rounded-full bg-[#3B2B23] text-white h-9 px-3" data-testid="creative-add">
            <Plus className="w-4 h-4" />
          </Button>
        </div>
        <label className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#8B5E3C] cursor-pointer">
          <input type="checkbox" checked={monthly} onChange={(e) => setMonthly(e.target.checked)} />
          Total mensal (agregado)
        </label>
      </div>
    </Card>
  );
}

function RevenueCard({ clientId, year, month, entries, onSaved }) {
  const [kind, setKind] = useState("total");
  const [amount, setAmount] = useState("");
  const [source, setSource] = useState("manual");

  const totalByKind = (k) => (entries || []).filter((e) => e.kind === k).reduce((s, e) => s + Number(e.amount || 0), 0);

  const add = async () => {
    if (!amount) return;
    try {
      await api.post(`/analytics/${clientId}/revenue`, {
        year, month, kind, amount: parseFloat(amount), source,
      });
      setAmount("");
      onSaved();
    } catch { toast.error("Erro a adicionar receita"); }
  };
  const del = async (id) => {
    try { await api.delete(`/analytics/revenue/${id}`); onSaved(); }
    catch { toast.error("Erro a apagar"); }
  };

  return (
    <Card className="card-soft p-5 border-[#EAE3DB]" data-testid="revenue-card">
      <SectionTitle>Receita</SectionTitle>
      <div className="grid grid-cols-3 gap-2 mb-4 text-center">
        <div>
          <div className="text-[9px] uppercase tracking-widest text-[#8B5E3C]">Orgânica</div>
          <div className="text-lg font-bold text-[#2C543B]">{eur(totalByKind("organic"))}</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-widest text-[#8B5E3C]">Paga</div>
          <div className="text-lg font-bold text-[#C07C5B]">{eur(totalByKind("paid"))}</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-widest text-[#8B5E3C]">Total</div>
          <div className="text-lg font-bold text-[#1A1412]">{eur(totalByKind("total"))}</div>
        </div>
      </div>
      <div className="space-y-1 max-h-32 overflow-y-auto mb-3">
        {(entries || []).map((e) => (
          <div key={e.id} className="flex items-center justify-between text-[11px] py-1 border-b border-[#EAE3DB]/60">
            <div className="uppercase tracking-widest text-[#8B5E3C]">{e.kind} · {e.source}</div>
            <div className="font-semibold text-[#3B2B23]">{eur(e.amount)}</div>
            <button onClick={() => del(e.id)} className="ml-2 text-red-700"><Trash2 className="w-3 h-3" /></button>
          </div>
        ))}
        {(!entries || entries.length === 0) && <div className="text-[11px] text-[#8B5E3C] italic">Sem receita registada</div>}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger className="h-9 bg-white border-[#EAE3DB]" data-testid="revenue-kind">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="organic">Orgânica</SelectItem>
            <SelectItem value="paid">Paga</SelectItem>
            <SelectItem value="total">Total</SelectItem>
          </SelectContent>
        </Select>
        <Select value={source} onValueChange={setSource}>
          <SelectTrigger className="h-9 bg-white border-[#EAE3DB]" data-testid="revenue-source">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="manual">Manual</SelectItem>
            <SelectItem value="ga4">GA4</SelectItem>
            <SelectItem value="meta_pixel">Meta Pixel</SelectItem>
            <SelectItem value="other">Outra</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex gap-2 mt-2">
        <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="€ 0,00" className="h-9 bg-white border-[#EAE3DB] flex-1" data-testid="revenue-amount" />
        <Button onClick={add} className="rounded-full bg-[#3B2B23] text-white h-9 px-3" data-testid="revenue-add">
          <Plus className="w-4 h-4" />
        </Button>
      </div>
    </Card>
  );
}

function CampaignsCard({ clientId, year, month, items, onSaved }) {
  const [showForm, setShowForm] = useState(false);
  const empty = {
    name: "", platform: "meta",
    start_date: `${year}-${String(month).padStart(2, "0")}-01`,
    end_date: "", budget: "", spend: "", revenue: "", status: "active", notes: "",
  };
  const [form, setForm] = useState(empty);
  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Nome obrigatório");
    try {
      await api.post(`/analytics/${clientId}/campaigns`, {
        ...form,
        budget: parseFloat(form.budget || 0),
        spend: parseFloat(form.spend || 0),
        revenue: parseFloat(form.revenue || 0),
      });
      setForm(empty);
      setShowForm(false);
      onSaved();
    } catch { toast.error("Erro a criar campanha"); }
  };
  const del = async (id) => {
    if (!confirm("Apagar campanha?")) return;
    try { await api.delete(`/analytics/campaigns/${id}`); onSaved(); }
    catch { toast.error("Erro a apagar"); }
  };

  return (
    <Card className="card-soft p-6 border-[#EAE3DB]" data-testid="campaigns-card">
      <div className="flex items-center justify-between mb-3">
        <SectionTitle>Campanhas pagas</SectionTitle>
        <Button
          onClick={() => setShowForm(!showForm)}
          className="rounded-full bg-[#3B2B23] text-white h-9 px-4 text-[10px] tracking-widest uppercase"
          data-testid="campaigns-toggle-form"
        >
          <Plus className="w-3 h-3 mr-1" /> {showForm ? "Fechar" : "Nova"}
        </Button>
      </div>
      {showForm ? (
        <div className="border border-[#EAE3DB] rounded-xl p-4 mb-4 space-y-3 bg-white">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input value={form.name} onChange={(e) => upd("name", e.target.value)} placeholder="Nome da campanha" className="bg-white border-[#EAE3DB]" data-testid="campaign-name" />
            <Select value={form.platform} onValueChange={(v) => upd("platform", v)}>
              <SelectTrigger className="bg-white border-[#EAE3DB]" data-testid="campaign-platform"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PLATFORMS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input type="date" value={form.start_date} onChange={(e) => upd("start_date", e.target.value)} className="bg-white border-[#EAE3DB]" data-testid="campaign-start" />
            <Input type="date" value={form.end_date} onChange={(e) => upd("end_date", e.target.value)} className="bg-white border-[#EAE3DB]" placeholder="Fim (opcional)" />
            <Input type="number" step="0.01" value={form.budget} onChange={(e) => upd("budget", e.target.value)} placeholder="Budget" className="bg-white border-[#EAE3DB]" />
            <Input type="number" step="0.01" value={form.spend} onChange={(e) => upd("spend", e.target.value)} placeholder="Gasto real" className="bg-white border-[#EAE3DB]" data-testid="campaign-spend" />
            <Input type="number" step="0.01" value={form.revenue} onChange={(e) => upd("revenue", e.target.value)} placeholder="Receita atribuída" className="bg-white border-[#EAE3DB]" data-testid="campaign-revenue" />
            <Select value={form.status} onValueChange={(v) => upd("status", v)}>
              <SelectTrigger className="bg-white border-[#EAE3DB]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Ativa</SelectItem>
                <SelectItem value="paused">Em pausa</SelectItem>
                <SelectItem value="ended">Terminada</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={submit} className="rounded-full bg-[#3B2B23] text-white h-10 px-6 text-[10px] tracking-widest uppercase" data-testid="campaign-save">
            Criar campanha
          </Button>
        </div>
      ) : null}
      <div className="overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-[#8B5E3C] border-b border-[#EAE3DB]">
              <th className="text-left py-2 pr-3">Campanha</th>
              <th className="text-left py-2 pr-3">Plataforma</th>
              <th className="text-right py-2 pr-3">Budget</th>
              <th className="text-right py-2 pr-3">Gasto</th>
              <th className="text-right py-2 pr-3">Receita</th>
              <th className="text-right py-2 pr-3">ROAS</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {(items || []).length === 0 ? (
              <tr><td colSpan="7" className="text-center py-6 text-[#8B5E3C] italic">Sem campanhas neste mês</td></tr>
            ) : items.map((c) => (
              <tr key={c.id} className="border-b border-[#EAE3DB]/60">
                <td className="py-2 pr-3 notranslate" translate="no">{c.name}</td>
                <td className="py-2 pr-3 uppercase text-[10px] tracking-widest text-[#8B5E3C]">{c.platform}</td>
                <td className="py-2 pr-3 text-right">{eur(c.budget)}</td>
                <td className="py-2 pr-3 text-right">{eur(c.spend)}</td>
                <td className="py-2 pr-3 text-right">{eur(c.revenue)}</td>
                <td className="py-2 pr-3 text-right font-semibold" style={{ color: c.roas >= 1 ? "#2C543B" : "#8A2A22" }}>
                  {(c.roas || 0).toFixed(2)}×
                </td>
                <td className="py-2 pr-3 text-right">
                  <button onClick={() => del(c.id)} className="text-red-700 hover:text-red-900" data-testid={`campaign-delete-${c.id}`}><Trash2 className="w-3 h-3" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function NetworksCard({ clientId, year, month, networks, onSaved }) {
  const active = Object.keys(NETWORK_META);
  const [openFor, setOpenFor] = useState(null);
  const [form, setForm] = useState({});

  const startEdit = (net) => {
    const existing = networks[net] || {};
    setForm({
      followers: existing.followers ?? "",
      reach: existing.reach ?? "",
      impressions: existing.impressions ?? "",
      engagement_rate: existing.engagement_rate ?? "",
    });
    setOpenFor(net);
  };
  const save = async () => {
    try {
      await api.put(`/analytics/${clientId}/metrics`, {
        network: openFor, year, month,
        followers: form.followers ? parseInt(form.followers, 10) : null,
        reach: form.reach ? parseInt(form.reach, 10) : null,
        impressions: form.impressions ? parseInt(form.impressions, 10) : null,
        engagement_rate: form.engagement_rate ? parseFloat(form.engagement_rate) : null,
        source: "manual",
      });
      setOpenFor(null);
      onSaved();
    } catch { toast.error("Erro a guardar métricas"); }
  };

  return (
    <Card className="card-soft p-6 border-[#EAE3DB]" data-testid="networks-card">
      <SectionTitle>Métricas por rede social</SectionTitle>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {active.map((net) => {
          const meta = NETWORK_META[net];
          const m = networks[net];
          const Icon = meta.Icon;
          return (
            <div key={net} className="rounded-xl border border-[#EAE3DB] bg-white p-4 space-y-2" data-testid={`network-${net}`}>
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: meta.color }}>
                  <Icon className="w-3.5 h-3.5 text-white" />
                </div>
                <div className="text-[13px] font-semibold text-[#1A1412]">{meta.label}</div>
              </div>
              {m ? (
                <div className="text-[11px] text-[#3B2B23] space-y-0.5">
                  <div>Seguidores: <strong>{(m.followers || 0).toLocaleString("pt-PT")}</strong>
                    {m.followers_delta_pct !== undefined && m.followers_delta_pct !== null ? (
                      <span className={`ml-2 text-[10px] ${m.followers_delta_pct >= 0 ? "text-emerald-700" : "text-red-700"}`}>{pct(m.followers_delta_pct)}</span>
                    ) : null}
                  </div>
                  <div>Reach: <strong>{(m.reach || 0).toLocaleString("pt-PT")}</strong></div>
                  <div>Impressões: <strong>{(m.impressions || 0).toLocaleString("pt-PT")}</strong></div>
                  {m.engagement_rate ? <div>Eng. rate: <strong>{Number(m.engagement_rate).toFixed(2)}%</strong></div> : null}
                </div>
              ) : (
                <div className="text-[11px] text-[#8B5E3C] italic">Sem dados este mês</div>
              )}
              {openFor === net ? (
                <div className="space-y-2 pt-2 border-t border-[#EAE3DB]">
                  <Input type="number" value={form.followers} onChange={(e) => setForm((s) => ({ ...s, followers: e.target.value }))} placeholder="Seguidores" className="h-8 text-[11px] bg-white border-[#EAE3DB]" />
                  <Input type="number" value={form.reach} onChange={(e) => setForm((s) => ({ ...s, reach: e.target.value }))} placeholder="Reach" className="h-8 text-[11px] bg-white border-[#EAE3DB]" />
                  <Input type="number" value={form.impressions} onChange={(e) => setForm((s) => ({ ...s, impressions: e.target.value }))} placeholder="Impressões" className="h-8 text-[11px] bg-white border-[#EAE3DB]" />
                  <Input type="number" step="0.01" value={form.engagement_rate} onChange={(e) => setForm((s) => ({ ...s, engagement_rate: e.target.value }))} placeholder="Engagement rate %" className="h-8 text-[11px] bg-white border-[#EAE3DB]" />
                  <div className="flex gap-1">
                    <Button variant="outline" onClick={() => setOpenFor(null)} className="h-8 flex-1 text-[9px] uppercase tracking-widest rounded-full">Cancelar</Button>
                    <Button onClick={save} className="h-8 flex-1 bg-[#3B2B23] text-white text-[9px] uppercase tracking-widest rounded-full" data-testid={`network-save-${net}`}>Guardar</Button>
                  </div>
                </div>
              ) : (
                <Button onClick={() => startEdit(net)} variant="outline" className="w-full h-8 text-[9px] uppercase tracking-widest rounded-full border-[#3B2B23] text-[#3B2B23]" data-testid={`network-edit-${net}`}>
                  Atualizar
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
